import React, { useState, useEffect } from 'react';

// reactstrap components
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from "reactstrap";

/**
 * Componente de filtros de búsqueda avanzada para notas
 * Sigue el mismo patrón de diseño compacto que SearchFilters de merchants
 * La búsqueda está limitada al flujo actual (originType/originId)
 */
function NotesSearchFilters({ 
  filters, 
  onFilterChange, 
  onSearch, 
  onClear, 
  isLoading = false,
  originType = null,
  originId = null 
}) {
  const [criteria, setCriteria] = useState([]);
  const todayStr = new Date().toISOString().slice(0, 10);

  // Campos disponibles para búsqueda (limitados al flujo actual)
  // originType y originId se pasan automáticamente desde el contexto
  const fieldOptions = [
    { value: '', label: 'Select Field' },
    { value: 'tag', label: 'Tag' },
    { value: 'createdBy', label: 'Created By' },
    { value: 'createdAt', label: 'Created Date (Range)' },
    { value: 'message', label: 'Message Content' },
  ];
  // Operadores eliminados para este flujo (no se usan)

  // Tipos de origen comunes (ya no se usan en la búsqueda, se pasan desde contexto)
  // const originTypeOptions = [
  //   { value: 'merchant', label: 'Merchant' },
  //   { value: 'application', label: 'Application' },
  //   { value: 'step', label: 'Workflow Step' },
  //   { value: 'main-menu', label: 'Main Menu' }
  // ];

  // Inicializar con criterio vacío
  useEffect(() => {
    if (criteria.length === 0) {
      addCriteria();
    }
  }, []);

  const addCriteria = () => {
    const newCriteria = {
      id: Date.now(),
      field: '',
      value: '',
      value2: '' // Para rangos de fecha
    };
    setCriteria([...criteria, newCriteria]);
  };

  const removeCriteria = (id) => {
    setCriteria(criteria.filter(c => c.id !== id));
  };

  const updateCriteria = (id, field, value) => {
    setCriteria(criteria.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const isCriterionValid = (c) => {
    if (!c || !c.field) return false;
    if (c.field === 'createdAt') {
      if (!c.value || !c.value2) return false;
      // No permitir fechas futuras y exigir rango válido
      if (c.value > todayStr || c.value2 > todayStr) return false;
      return c.value <= c.value2;
    }
    return Boolean(c.value && String(c.value).trim() !== '');
  };

  const handleSearch = () => {
    const validCriteria = criteria.filter(isCriterionValid);
    if (validCriteria.length === 0) {
      onClear();
      return;
    }

    const searchFilters = {};
    validCriteria.forEach(c => {
      const val = String(c.value || '').slice(0, 255);
      if (c.field === 'createdAt') {
        searchFilters.createdAt = { value: c.value || '', value2: c.value2 || '' };
      } else if (c.field === 'tag') {
        const parts = val.split(',').map(t => t.trim()).filter(Boolean).slice(0, 50);
        if (parts.length > 0) searchFilters.userTags = parts;
      } else if (c.field === 'createdBy') {
        searchFilters.createdBy = val;
      } else if (c.field === 'message') {
        searchFilters.message = val;
      } else if (c.field === 'originId') {
        searchFilters.originId = val;
      }
    });

    // Agregar contexto del flujo actual automáticamente
    if (originType) searchFilters.originType = originType;
    // Solo agregar originId del contexto si no se especificó manualmente en el filtro
    if (originId && !searchFilters.originId) searchFilters.originId = originId;
    if (filters && filters.tag) searchFilters.tag = filters.tag; // fallback if parent provides tag

    onFilterChange(searchFilters);
    onSearch(searchFilters);
  };

  const handleClear = () => {
    setCriteria([{ id: Date.now(), field: '', value: '', value2: '' }]);
    onClear();
  };
  // Operadores removidos

  const getValueInput = (criterion) => {
    const { id, field, value, value2 } = criterion;
    
    if (field === 'createdAt') {
      return (
        <Row>
          <Col md="6">
            <Label className="mb-1 small text-muted">From:</Label>
            <Input
              type="date"
              size="sm"
              value={value || ''}
              max={todayStr}
              onChange={(e) => updateCriteria(id, 'value', e.target.value)}
              placeholder="From date"
            />
          </Col>
          <Col md="6">
            <Label className="mb-1 small text-muted">To:</Label>
            <Input
              type="date"
              size="sm"
              value={value2 || ''}
              min={value || ''}
              max={todayStr}
              onChange={(e) => updateCriteria(id, 'value2', e.target.value)}
              placeholder="To date"
            />
          </Col>
        </Row>
      );
    }

    // originType ya no se usa en la búsqueda, se pasa automáticamente desde el contexto

    if (field === 'message') {
      return (
        <Input
          type="textarea"
          size="sm"
          rows="2"
          value={value || ''}
          onChange={(e) => updateCriteria(id, 'value', e.target.value.slice(0, 255))}
          placeholder="Enter text to search in message content..."
        />
      );
    }

    return (
      <div>
        {field === 'tag' && (
          <small className="text-muted d-block mb-1">Separate multiple tags with commas.</small>
        )}
        <Input
          type="text"
          size="sm"
          value={value || ''}
          onChange={(e) => updateCriteria(id, 'value', e.target.value.slice(0, 255))}
          placeholder={field === 'tag' ? 'tag1, tag2, tag3' : `Enter ${fieldOptions.find(f => f.value === field)?.label?.toLowerCase()}...`}
        />
      </div>
    );
  };

  const hasValidCriteria = () => {
    return criteria.some(isCriterionValid);
  };

  return (
    <div>
      {/* Search Criteria Section */}
      <div className="mb-2">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 className="mb-0 text-muted small">
            <i className="ni ni-zoom-split-in mr-1"></i>
            Search Criteria
          </h6>
        </div>

        {/* Criteria Rows */}
        {criteria.length === 0 ? (
          <div className="text-center py-2 border rounded bg-light">
            <i className="ni ni-zoom-split-in text-muted" style={{ fontSize: '1rem' }}></i>
            <p className="text-muted mt-1 mb-0 small">All notes shown by default. Add criteria to filter.</p>
          </div>
        ) : (
          <div className="border rounded bg-light" style={{ maxWidth: '900px' }}>
            {/* Table Headers */}
            <div className="p-2 border-bottom bg-white">
              <Row>
                <Col md="4">
                  <small className="text-muted font-weight-bold">Field</small>
                </Col>
                <Col md="8">
                  <small className="text-muted font-weight-bold">Value</small>
                </Col>
              </Row>
            </div>
            
            {/* Criteria Rows */}
            <div className="p-2">
              {criteria.map((criterion, index) => (
                <Row key={criterion.id} className={`align-items-end ${index > 0 ? 'mt-1' : ''}`}>
                  <Col md="4">
                    <FormGroup className="mb-0">
                      <Input
                        type="select"
                        size="sm"
                        value={criterion.field}
                        onChange={(e) => updateCriteria(criterion.id, 'field', e.target.value)}
                      >
                        {fieldOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="8">
                    <FormGroup className="mb-0">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          {getValueInput(criterion)}
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 ml-1"
                          onClick={() => removeCriteria(criterion.id)}
                          title="Remove criteria"
                          disabled={criteria.length === 1}
                        >
                          <i className="ni ni-fat-remove text-danger"></i>
                        </Button>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Criteria Button */}
      <div className="mb-2">
        <Button
          color="outline-primary"
          size="sm"
          onClick={addCriteria}
        >
          <i className="ni ni-fat-add mr-1"></i>
          Add Criteria
        </Button>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end">
        <Button
          color="warning"
          className="mr-2"
          onClick={handleClear}
          disabled={isLoading}
        >
          Clear
        </Button>
        <Button
          color="primary"
          onClick={handleSearch}
          disabled={criteria.length === 0 || isLoading || !hasValidCriteria()}
        >
          {isLoading ? 'Searching...' : `Search (${criteria.length} criteria)`}
        </Button>
      </div>
    </div>
  );
}

export default NotesSearchFilters;
